import { find, includes } from 'lodash';

import type { AppData } from '../types/app_data';
import { CreditRange } from '../types/credit_range.enum';
import { growthBookInstance as growthBook } from '../utils/analytics/growthbook';

import { ABTestMercuryQuestion } from './enabled_features/ab_tests';

/**
 * Returns true if user is eligible for Geico quotes.
 *
 * https://www.notion.so/jerrydesign/Geico-Quoting-Display-Logic-b3143487ab9f4f3fb079fe8497597a92#c6114ee710bf43c4bb8f9104ea8a2fd3
 *
 * - User selected insurance service and not signup for OnboardingLite.
 * - User is not eligible if they have a credit range of less than 550 or not sure and have not insured before.
 */
export const isEligibleForGeicoQuotes = (appData: AppData): boolean => {
  const state = appData?.address?.state;
  const cars = appData?.cars;
  const creditRange = appData?.signupParams?.creditRange;
  const isAutoInsured = appData?.signupParams?.isAutoInsured;

  if (!appData.selectedService?.insurance) {
    return false;
  }

  if (appData.isOnboardingLite) {
    return false;
  }

  if (!state) {
    return false;
  }

  if (!cars?.length) {
    return false;
  }

  const isCreditRangeIneligibleNotInCA =
    state !== 'CA' && (!creditRange || creditRange === CreditRange.LessThan550 || creditRange === CreditRange.NotSure);

  const isCreditRangeIneligibleInCA =
    state === 'CA' &&
    (!creditRange ||
      creditRange === CreditRange.LessThan550 ||
      creditRange === CreditRange.Between550and659 ||
      creditRange === CreditRange.NotSure);

  const isCreditRangeIneligible = isCreditRangeIneligibleNotInCA || isCreditRangeIneligibleInCA;

  if (isCreditRangeIneligible && !isAutoInsured) {
    return false;
  }

  return true;
};

/**
 * Return true if user is eligible for Mercury quotes.
 * That is:
 *  - User is in OK, IL, or CA.
 *  - User has at least one car that is not used for ridesharing, and not used for delivery.
 * https://app.asana.com/0/1201845956131908/1209117525690765/f
 */
export const isEligibleForMercuryQuotes = (appData: AppData): boolean => {
  const state = appData?.address?.state;
  const cars = appData?.cars;

  if (appData.signupParams?.needNNOInsurance === true) {
    return false;
  }

  if (!appData.selectedService?.insurance) {
    return false;
  }

  if (appData.isOnboardingLite) {
    return false;
  }

  return (
    includes(['OK', 'IL', 'CA'], state) &&
    !!find(cars, (car) => !car.isArchived && !car.ridesharing && !car.usedForDelivery) &&
    !!growthBook?.getFeatureValue(ABTestMercuryQuestion.storageKey, false)
  );
};
